import { Link } from 'react-router-dom'
import { ChoiceGroup, Switch } from '@/components/Form/Form'
import { Paper, Section } from '@/components/Layout/Layout'
import { useCopy } from '@/i18n/useCopy'
import { LOCALES, LOCALE_CODES } from '@/i18n/locales'
import type { LocaleCode } from '@/i18n/locales'
import { PERSONAS, PERSONA_IDS } from '@/persona/personas'
import type { PersonaId } from '@/persona/personas'
import { useProfileStore } from '@/store/profileStore'
import { useSettingsStore } from '@/store/settingsStore'
import type { Appearance } from '@/store/settingsStore'
import { voiceSession } from '@/voice/sessionController'
import styles from './Settings.module.css'

/**
 * Settings, kept short.
 *
 * Every hint line has been removed: a label that needs a sentence under it to
 * be understood is a label that needs rewriting, and a settings page a person
 * has to read is one they will not finish. What is left is a choice per row.
 *
 * The endpointing control that used to live here is gone at the product
 * owner's request. The setting itself still exists and the conversation still
 * reads it, so the behaviour is unchanged at its default; what has gone is the
 * ability to widen it, which was the accessibility lever for anyone the
 * silence detector cuts off mid-word. Noted in the README's known limits.
 */
export function SettingsScreen() {
  const copy = useCopy()
  const settings = useSettingsStore()

  return (
    <Paper title={copy.ui.settings.title} backTo="/">
      <Section>
        <ChoiceGroup<LocaleCode>
          label={copy.ui.settings.language}
          value={settings.locale}
          choices={LOCALE_CODES.map((code) => ({
            value: code,
            label: LOCALES[code].nativeName,
            // A language nobody has reviewed is labelled as such rather than
            // presented as finished work.
            ...(LOCALES[code].reviewed ? {} : { note: copy.ui.common.preview }),
          }))}
          onChange={(next) => {
            settings.chooseLocale(next)
            useProfileStore.getState().setLanguage(next)
            voiceSession.setLocale(next)
          }}
        />
      </Section>

      <Section>
        {/*
         * Who answers, rather than "voice". The two are the same choice today
         * and they will not stay that way: a persona is a name, a manner and a
         * voice together, and labelling it by the one part that comes out of a
         * speaker would make a third entry read as a third accent.
         *
         * The hint is the chosen persona's own opening line, in Hausa, which
         * is the closest thing to a preview this screen can honestly offer.
         * Hearing one costs a route through our own server that does not exist
         * yet, and a browser voice reading it would be a preview of the wrong
         * voice. Reading the line at least shows the difference the names hide:
         * Nadia says "ni ce", Salim says "ni ne".
         */}
        <ChoiceGroup<PersonaId>
          label={copy.ui.settings.persona}
          hint={PERSONAS[settings.persona].greeting}
          value={settings.persona}
          choices={PERSONA_IDS.map((id) => ({ value: id, label: PERSONAS[id].name }))}
          onChange={(next) => {
            settings.set('persona', next)
            // Immediately, mid-conversation, and the session decides what that
            // means for a turn already being spoken.
            voiceSession.setPersona(next)
          }}
        />
      </Section>

      <Section>
        <ChoiceGroup<Appearance>
          label={copy.ui.settings.appearance}
          value={settings.appearance}
          choices={[
            { value: 'light', label: copy.ui.settings.appearanceLight },
            { value: 'dark', label: copy.ui.settings.appearanceDark },
            { value: 'system', label: copy.ui.settings.appearanceSystem },
          ]}
          onChange={(next) => settings.set('appearance', next)}
        />

        <Switch
          label={copy.ui.settings.captions}
          checked={settings.captionsAlwaysOn}
          onChange={(next) => settings.set('captionsAlwaysOn', next)}
        />

        <Switch
          label={copy.ui.settings.reduceMotion}
          checked={settings.reduceMotion}
          onChange={(next) => settings.set('reduceMotion', next)}
        />

        <Switch
          label={copy.ui.settings.haptics}
          checked={settings.haptics}
          onChange={(next) => settings.set('haptics', next)}
        />
      </Section>

      <Section>
        <Link className={styles.row} to="/diagnostics">
          {copy.ui.settings.diagnostics}
        </Link>
        <Link className={styles.row} to="/terms">
          {copy.ui.settings.terms}
        </Link>
      </Section>
    </Paper>
  )
}
